import React from 'react';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Box,
  Checkbox,
  InputAdornment,
  SvgIconProps,
  TextField
} from '@mui/material';
import { Sector } from '@esg/esg-global-types';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { getSectors, getUnconfiguredSectors, sector_label } from '../../../../lib/app/sector';

interface SectorSelectProps {
  handleChangeSectors?: (sectors: Array<Sector> | Sector | null) => void;
  class_name?: string;
  options?: Array<Sector>;
  selected_options?: Array<Sector> | Sector | null;
  disabled?: boolean;
  input_label?: string;
  multi_select?: boolean;
  tag_limit?: number;
  select_limit?: number;
  group_id?: string;
  company_id?: string;
  unconfigured_only?: boolean;
  master_list?: boolean;
  start_adornment?: React.ReactElement<SvgIconProps>;
  auto_select?: boolean;
  onClick?: () => void;
}

// Checkbox icons
const check_icon_blank = <CheckBoxOutlineBlankIcon fontSize="small" />;
const check_icon_filled = <CheckBoxIcon fontSize="small" />;

/**
 * Sector selection auto-complete component that handles a variety of built-in scenarios.
 * @param {Array<Sector> | Sector | null} props.handleChangeSectors Callback function to notify the parent component when the selected options have changed.
 * @param {Array<Sector>} props.options Custom list of options provided by the parent component. Defaults to none.
 * @param {Array<Sector> | Sector | null} props.selected_options Custom list of selected options provided by the parent component. Changes component from an uncontrolled to controlled.
 * @param {boolean} props.disabled Renders the component in a disabled state along with any other provided components. Defaults to enabled.
 * @param {string} props.input_label Sets a custom component input label. Defaults to the singular version of the main entity name.
 * @param {boolean} props.multi_select Allow multi option selection and keeps the select open while selecting. Defaults to a single select and closes after selecting an option.
 * @param {number} props.tag_limit Sets a limit on the amount of tags rendered in the input. Defaults to unlimited.
 * @param {number} props.select_limit Sets a limit on the amount of options the user can select from the autocomplete. Defaults to unlimited.
 * @param {string} props.group_id Query configured sectors for the provided group. Works in conjunction with props.company_id.
 * @param {string} props.company_id Query configured sectors for the provided company. Works in conjunction with props.group_id.
 * @param {boolean} props.unconfigured_only Query only unconfigured sectors for the provided group_id and company_id. Defaults to false.
 * @param {boolean} props.master_list Forcefully query sectors from the master list. Does not require group_id and company_id. Defaults to false.
 * @param {React.ReactElement<SvgIconProps>} props.start_adornment Set an input icon for the autocomplete.
 * @param {boolean} props.auto_select Pre-populate the autocomplete input when only one sector is available.
 */
const SectorSelect = (props: SectorSelectProps) => {
  const [optionList, setOptions] = React.useState<Array<Sector>>([]);
  const [selectedOptions, setSelectedOptions] = React.useState<Array<Sector> | Sector | null>(
    props.multi_select ? [] : null
  );

  // Query validations
  const get_unconfigured_sectors: boolean =
    props.options === undefined &&
    !props.master_list &&
    props.group_id &&
    props.company_id &&
    props.unconfigured_only
      ? true
      : false;

  // Define autocomplete options
  React.useEffect(() => {
    (async () => {
      const sectors: Array<Sector> = props.options
        ? props.options
        : props.master_list
          ? await getSectors(true)
          : get_unconfigured_sectors
            ? await getUnconfiguredSectors(props.group_id || '', props.company_id || '')
            : props.group_id && props.company_id
              ? await getSectors(false, props.group_id, props.company_id)
              : [];
      if (sectors) {
        setOptions(sectors);
      }
      // When auto-select is true, automatically populate the autocomplete when only one sector is present.
      if (props.auto_select && sectors.length === 1) {
        setSelectedOptions(sectors[0]);
        props.handleChangeSectors && props.handleChangeSectors(sectors[0]);
      }
    })();
  }, [props.group_id, props.company_id]);

  // Define selected options
  React.useEffect(() => {
    setSelectedOptions(
      props.multi_select
        ? Array.isArray(props.selected_options)
          ? props.selected_options
          : []
        : props.selected_options
          ? props.selected_options
          : null
    );
  }, [props.options]);

  return (
    <Box id="sector-select" onClick={props.onClick}>
      <Autocomplete
        fullWidth
        disabled={props.disabled}
        limitTags={props.tag_limit}
        options={optionList}
        value={selectedOptions}
        getOptionLabel={(option: Sector) => {
          return option.name;
        }}
        disableCloseOnSelect={props.multi_select}
        isOptionEqualToValue={(option: Sector, value: Sector) => option.id === value.id}
        multiple={props.multi_select}
        noOptionsText={`No ${sector_label.many.toLowerCase()}`}
        getOptionDisabled={(option) =>
          !!(
            props.select_limit &&
            Array.isArray(selectedOptions) &&
            selectedOptions.length >= props.select_limit &&
            !selectedOptions.find((selected_option: Sector) => selected_option.id === option.id)
          )
        }
        renderOption={(render_props, option: Sector, { selected }) => (
          <Box
            component="li"
            sx={{ display: 'flex', alignItems: 'center' }}
            {...render_props}
            key={option.id}
          >
            {props.multi_select && (
              <Checkbox
                icon={check_icon_blank}
                checkedIcon={check_icon_filled}
                style={{ marginRight: 8 }}
                checked={selected}
              />
            )}
            {option.name}
          </Box>
        )}
        renderInput={(params: AutocompleteRenderInputParams) => (
          <TextField
            {...params}
            label={`${props.input_label ?? sector_label.one}${
              props.multi_select &&
              props.select_limit &&
              Array.isArray(selectedOptions) &&
              selectedOptions.length >= props.select_limit
                ? ` - ${props.select_limit} Max Selection Limit Reached`
                : ''
            }`}
            variant="standard"
            InputProps={{
              ...params.InputProps,
              startAdornment: props.start_adornment ? (
                <>
                  <InputAdornment position="start">{props.start_adornment}</InputAdornment>
                  {params.InputProps.startAdornment}
                </>
              ) : (
                <>{params.InputProps.startAdornment}</>
              )
            }}
          />
        )}
        onChange={(
          event: React.SyntheticEvent<Element, Event>,
          value: Array<Sector> | Sector | null
        ) => {
          setSelectedOptions(value);
          props.handleChangeSectors && props.handleChangeSectors(value);
        }}
      />
    </Box>
  );
};

export default SectorSelect;
